import React, { useEffect, useMemo, useState } from 'react';
import { getBusinessPartnerTabs } from './content';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import Loader from '@components/loader/Loader';
import { IBPGeneralData, IBusinessPartnerInfo } from '.';
import TabsLayout from '@components/layout/tabs/TabsLayout';
import { useAppSelector } from '@redux/hooks';
import { getBusinessPartnerDetailsInfo } from './queries';
import { entityToBusinessPartnerDetails } from './utils';
import BusinessPartnerEntityInfoWidget from './widgets/BusinessPartnerEntityInfoWidget';
import SummaryTab from './tabs/summary/SummaryTab';
import VignetteRangeTab from './tabs/vignette-range/VignetteRangeTab';
import BusinessPartnerPaymentTermsTab from './tabs/payment-terms/BusinessPartnerPaymentTermsTab';
import CommissionAndLimitsTab from './tabs/commissions-and-limits/CommissionAndLimitsTab';
import BusinessPartnerStepper from './widgets/stepper/BusinessPartnerStepper';

const SalesforceBusinessPartnerDetailsPage: React.FC = () => {
  const { id: currentBusinessPartnerId } = useParams<{ id: string }>();
  const user = useAppSelector((state) => state.user);

  const {
    refetch,
    data: bpData,
    loading,
  } = useQuery(getBusinessPartnerDetailsInfo, {
    variables: { id: currentBusinessPartnerId },
    errorPolicy: 'all',
    skip: !currentBusinessPartnerId,
  });

  const [businessPartnerDetailsInfo, setBusinessPartnerDetailsInfo] =
    useState<IBusinessPartnerInfo>();

  const generalData: IBPGeneralData = useMemo(() => {
    if (!businessPartnerDetailsInfo) {
      return {
        userIsAdmin: false,
        businessPartner: {
          id: '',
          isActive: false,
          isDirectSalesforce: false,
        },
      };
    }

    return {
      userIsAdmin: user.userRoles.includes('Insurance-Admin'),
      businessPartner: {
        id: currentBusinessPartnerId,
        isActive: businessPartnerDetailsInfo.status === 'active',
        isDirectSalesforce:
          businessPartnerDetailsInfo?.businessPartnerType ===
          'Direct_Salesforce',
      },
    };
  }, [businessPartnerDetailsInfo, currentBusinessPartnerId, user]);

  useEffect(() => {
    if (bpData) {
      const entityDetails = entityToBusinessPartnerDetails(bpData);
      setBusinessPartnerDetailsInfo(entityDetails);
    }
  }, [bpData]);

  const tabs = () => {
    const businessPartnerTabs = getBusinessPartnerTabs(
      businessPartnerDetailsInfo?.businessPartnerType
    );

    businessPartnerTabs.tabs[0].widgets[0].children = (
      <SummaryTab
        generalData={generalData}
        onSuccess={() => refetch()}
        businessPartnerDetails={businessPartnerDetailsInfo}
      />
    );

    businessPartnerTabs.tabs[1].widgets[0].children = (
      <CommissionAndLimitsTab
        generalData={generalData}
        onSuccess={() => refetch()}
        businessPartnerDetails={businessPartnerDetailsInfo}
      />
    );

    businessPartnerTabs.tabs[2].widgets[0].children = (
      <VignetteRangeTab generalData={generalData} onSuccess={() => refetch()} />
    );

    businessPartnerTabs.tabs[3].widgets[0].children = (
      <BusinessPartnerPaymentTermsTab
        businessPartnerId={currentBusinessPartnerId}
      />
    );

    return businessPartnerTabs;
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <TabsLayout
      name="policyPageDetails"
      layout={tabs()}
      leftChildren={
        businessPartnerDetailsInfo ? (
          <BusinessPartnerEntityInfoWidget data={businessPartnerDetailsInfo} />
        ) : (
          <></>
        )
      }
      mainChildren={
        <BusinessPartnerStepper
          businessPartnerInfo={businessPartnerDetailsInfo}
          onSuccess={() => {}}
        />
      }
    />
  );
};

export default SalesforceBusinessPartnerDetailsPage;
